import { createContext, useState, useContext, ReactNode } from "react";
import {
  useExternalSuppliersControllerAuthenticateSupplier,
  setAuthToken,
  AUTH_TOKEN_KEY,
} from "@semo/api-contracts";
import type {
  AuthSupplierRequestDto,
  SupplierInfoDto,
} from "@semo/api-contracts";

interface Supplier {
  id: string; // Backend supplierId (UUID)
  supplierId: string; // C-Market ID
  companyName: string;
  role: "master" | "user";
}

interface SupplierAuthContextType {
  currentSupplier: Supplier | null;
  isLoading: boolean;
  login: (supplierInfo: SupplierInfoDto) => Promise<boolean>;
  logout: () => void;
}

const SupplierAuthContext = createContext<SupplierAuthContextType | undefined>(
  undefined,
);

export const SupplierAuthProvider = ({ children }: { children: ReactNode }) => {
  const [currentSupplier, setCurrentSupplier] = useState<Supplier | null>(null);

  // Use Orval-generated React Query hook for type-safe API call
  const authMutation = useExternalSuppliersControllerAuthenticateSupplier();

  const login = async (supplierInfo: SupplierInfoDto) => {
    try {
      const authRequest: AuthSupplierRequestDto = {
        supplierInfo: supplierInfo,
      };

      const response = await authMutation.mutateAsync({ data: authRequest });

      // Extract data from BaseResponseDto wrapper
      const responseData = response.data;
      if (!responseData?.supplierId) {
        throw new Error("Invalid response: missing supplierId");
      }

      // ✅ Bearer Token Handling
      if (responseData.accessToken) {
        localStorage.setItem(AUTH_TOKEN_KEY, responseData.accessToken);
        setAuthToken(responseData.accessToken);
      }

      const supplier: Supplier = {
        id: responseData.supplierId,
        supplierId: supplierInfo.supplierId,
        companyName: supplierInfo.companyName,
        role: supplierInfo.supplierId.includes("MASTER") ? "master" : "user",
      };

      setCurrentSupplier(supplier);
      return true;
    } catch (error) {
      console.error("Login failed:", error);
      return false;
    }
  };

  const logout = () => {
    localStorage.removeItem(AUTH_TOKEN_KEY);
    setCurrentSupplier(null);
  };

  return (
    <SupplierAuthContext.Provider
      value={{
        currentSupplier,
        isLoading: authMutation.isPending,
        login,
        logout,
      }}
    >
      {children}
    </SupplierAuthContext.Provider>
  );
};

export const useSupplierAuth = () => {
  const context = useContext(SupplierAuthContext);
  if (!context)
    throw new Error("useSupplierAuth must be used within SupplierAuthProvider");
  return context;
};
